import React, { useEffect, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Select,
  MenuItem,
  Typography,
  Checkbox,
} from "@mui/material";
import PersonaAutocomplete from "./InfoInvoice/PersonaAutocomplete";

const tdLabelSx = {
  width: 200,
  bgcolor: "#f3f3f3",
  borderRight: "1px solid #e0e0e0",
  color: "#333",
  fontWeight: 600,
  verticalAlign: "top",
};

const tdFieldSx = { p: 1, verticalAlign: "middle" };

export default function InfoInvoice({ onChange }) {
  const [data, setData] = useState({
    fecha_emision: "",
    tipo_documento: "Cliente",
    tipo_persona: "CLIENTE",
    tipo_comprobante: "Factura",
    puntos_emision: [],
    establecimiento: "",
    punto_emision: "",
    numero_documento: "",
    persona: null,
    referencia: "",
    vendedor: "",
    vencimiento_dias: "0",
    entregado: true,
  });

  const [fechaError, setFechaError] = useState(false);
  const [vendedores, setVendedores] = useState([]);

  useEffect(() => {
    const cargarDetalle = async () => {
      try {
        const res = await fetch("/facturas/documento/detalle/", {
          credentials: "include",
        });
        const json = await res.json();
        if (res.ok) {
          const puntos = json.puntos_emision || [];
          const primero = puntos[0] || {};
          setData((prev) => ({
            ...prev,
            fecha_emision: json.fecha_emision || "",
            tipo_comprobante: json.tipo_comprobante || "Factura",
            puntos_emision: puntos,
            establecimiento: primero.establecimiento || "",
            punto_emision: primero.punto_emision || "",
            numero_documento: primero.numero_documento || "",
          }));
        }
      } catch {}
    };

    const cargarVendedores = async () => {
      try {
        const r = await fetch("/facturas/usuarios/activos/", {
          credentials: "include",
        });
        const j = await r.json();
        if (r.ok && Array.isArray(j.vendedores)) setVendedores(j.vendedores);
        else setVendedores([]);
      } catch {
        setVendedores([]);
      }
    };

    cargarDetalle();
    cargarVendedores();
  }, []);

  useEffect(() => {
    onChange?.(data);
  }, [data, onChange]);

  const handleFechaChange = (e) => {
    const value = e.target.value;
    const regex = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/;
    setFechaError(value ? !regex.test(value) : false);
    setData((prev) => ({ ...prev, fecha_emision: value }));
  };

  const handlePuntoChange = (e) => {
    const seleccionado = e.target.value;
    const punto = data.puntos_emision.find(
      (p) => p.punto_emision === seleccionado
    );
    setData((prev) => ({
      ...prev,
      punto_emision: seleccionado,
      numero_documento: punto ? punto.numero_documento : "",
    }));
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: "#333" }}>
        Información del Documento
      </Typography>

      <Table>
        <TableBody>
          <TableRow>
            <TableCell sx={tdLabelSx}>Fecha de Emisión:</TableCell>
            <TableCell sx={tdFieldSx}>
              <TextField
                value={data.fecha_emision}
                onChange={handleFechaChange}
                error={fechaError}
                helperText={fechaError ? "Formato inválido (dd/mm/aaaa)" : ""}
                placeholder="dd/mm/aaaa"
                inputProps={{ maxLength: 10 }}
                sx={{ minWidth: 280 }}
              />
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={tdLabelSx}>Tipo de Documento:</TableCell>
            <TableCell sx={tdFieldSx}>
              <Select
                value={data.tipo_persona}
                onChange={(e) =>
                  setData((prev) => ({
                    ...prev,
                    tipo_persona: e.target.value,
                    persona: null,
                  }))
                }
                sx={{ minWidth: 280 }}
              >
                <MenuItem value="CLIENTE">Cliente</MenuItem>
                <MenuItem value="PROVEEDOR">Proveedor</MenuItem>
              </Select>
            </TableCell>
            <TableCell sx={{ ...tdFieldSx, pl: 2 }}>
              <Select value={data.tipo_comprobante} sx={{ minWidth: 280 }} disabled>
                <MenuItem value="Factura">Factura</MenuItem>
              </Select>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={tdLabelSx}>Establecimiento:</TableCell>
            <TableCell sx={tdFieldSx}>
              <TextField
                value={data.establecimiento}
                InputProps={{ readOnly: true }}
                sx={{ minWidth: 280 }}
              />
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={tdLabelSx}>P. Emisión:</TableCell>
            <TableCell sx={tdFieldSx}>
              <Select
                value={data.punto_emision}
                onChange={handlePuntoChange}
                sx={{ minWidth: 280 }}
              >
                {data.puntos_emision.map((p, i) => (
                  <MenuItem key={i} value={p.punto_emision}>
                    {p.punto_emision}
                  </MenuItem>
                ))}
              </Select>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={tdLabelSx}># de Documento:</TableCell>
            <TableCell sx={tdFieldSx}>
              <TextField
                value={data.numero_documento}
                InputProps={{ readOnly: true }}
                sx={{ minWidth: 280 }}
              />
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={tdLabelSx}>Persona:</TableCell>
            <TableCell sx={tdFieldSx}>
              <PersonaAutocomplete
                value={data.persona}
                tipoDocumento={data.tipo_persona}
                onChange={(persona) =>
                  setData((prev) => ({ ...prev, persona }))
                }
              />
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={tdLabelSx}>Referencia:</TableCell>
            <TableCell sx={tdFieldSx}>
              <TextField
                value={data.referencia}
                onChange={(e) =>
                  setData((prev) => ({ ...prev, referencia: e.target.value }))
                }
                sx={{ minWidth: 350 }}
              />
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={tdLabelSx}>Vendedor:</TableCell>
            <TableCell sx={tdFieldSx}>
              <Select
                value={data.vendedor}
                displayEmpty
                onChange={(e) =>
                  setData((prev) => ({ ...prev, vendedor: e.target.value }))
                }
                sx={{ minWidth: 280 }}
              >
                <MenuItem value="">
                  <em>Seleccionar</em>
                </MenuItem>
                {vendedores.map((v) => (
                  <MenuItem key={v.id} value={v.id}>
                    {v.nombre}
                  </MenuItem>
                ))}
              </Select>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={tdLabelSx}>Vencimiento:</TableCell>
            <TableCell sx={tdFieldSx}>
              <TextField
                value={data.vencimiento_dias}
                onChange={(e) =>
                  setData((prev) => ({
                    ...prev,
                    vencimiento_dias:
                      e.target.value.replace(/\D/g, "").slice(0, 3) || "0",
                  }))
                }
                inputProps={{ inputMode: "numeric", maxLength: 3 }}
                sx={{ width: 80 }}
              />
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={tdLabelSx}>Entregado:</TableCell>
            <TableCell sx={tdFieldSx}>
              <Checkbox
                checked={data.entregado}
                onChange={(e) =>
                  setData((prev) => ({
                    ...prev,
                    entregado: e.target.checked,
                  }))
                }
              />
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  );
}
